import { HttpErrorResponse } from '@angular/common/http';
import { isolateLtr } from '../common/i18n/i18n-helpers';
import { CatalogAction, CatalogSort, CourseLevel, CourseStatus, CourseVisibility, EnrollmentSource, EnrollmentStatus } from '../models/course';

// The bidi isolate helper is shared with the non-LMS screens; re-exported so the many
// existing `from '../lms-utils'` imports keep working.
export { isolateLtr };

/** First API error in the current language (ApiResponse errors are { messageEn, messageAr }). */
export function apiErrorMessage(source: unknown, lang: string, fallback: string): string {
  const body: any = source instanceof HttpErrorResponse ? source.error : source;
  const first = body?.errors?.[0];
  if (!first) return fallback;
  if (typeof first === 'string') return first;
  return (lang === 'ar' ? first.messageAr : first.messageEn) || first.messageEn || fallback;
}

export function localized(lang: string, en?: string | null, ar?: string | null): string {
  return (lang === 'ar' ? ar || en : en || ar) ?? '';
}

export const STATUS_KEYS: Record<CourseStatus, string> = {
  [CourseStatus.Draft]: 'courses.StatusDraft',
  [CourseStatus.Published]: 'courses.StatusPublished',
  [CourseStatus.Archived]: 'courses.StatusArchived',
};

export const VISIBILITY_KEYS: Record<CourseVisibility, string> = {
  [CourseVisibility.Tenant]: 'courses.VisibilityTenant',
  [CourseVisibility.Restricted]: 'courses.VisibilityRestricted',
  [CourseVisibility.Hidden]: 'courses.VisibilityHidden',
};

export const LEVEL_KEYS: Record<CourseLevel, string> = {
  [CourseLevel.AllLevels]: 'courses.LevelAll',
  [CourseLevel.Beginner]: 'courses.LevelBeginner',
  [CourseLevel.Intermediate]: 'courses.LevelIntermediate',
  [CourseLevel.Advanced]: 'courses.LevelAdvanced',
};

export const LANGUAGE_KEYS: Record<string, string> = {
  ar: 'courses.LangAr',
  en: 'courses.LangEn',
  'ar-en': 'courses.LangBilingual',
};

/**
 * "239.3 KB". The value is wrapped in an LRI…PDI bidi isolate so the number keeps its
 * unit in Arabic: without it the RTL paragraph splits "239.3" and "KB" into two runs and
 * renders them as "KB 239.3". The isolates are zero-width formatting characters, so the
 * visible text is unchanged.
 */
export function formatSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  if (!bytes) return isolateLtr('0 B');
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return isolateLtr(`${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`);
}

/** Client-side mirror of the server rule: only https URLs are ever bound to an iframe. */
export function isHttpsUrl(url?: string | null): boolean {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

// ── L2: enrollment & progress ─────────────────────────────────────────────
export const ENROLLMENT_STATUS_KEYS: Record<EnrollmentStatus, string> = {
  [EnrollmentStatus.Pending]: 'courses.EnrollmentPending',
  [EnrollmentStatus.Active]: 'courses.EnrollmentActive',
  [EnrollmentStatus.Completed]: 'courses.EnrollmentCompleted',
  [EnrollmentStatus.Dropped]: 'courses.EnrollmentDropped',
};

export const ENROLLMENT_SOURCE_KEYS: Record<EnrollmentSource, string> = {
  [EnrollmentSource.GradeGroupAuto]: 'courses.SourceGradeGroupAuto',
  [EnrollmentSource.Manual]: 'courses.SourceManual',
  [EnrollmentSource.SelfEnrollCode]: 'courses.SourceSelfEnrollCode',
  [EnrollmentSource.Approval]: 'courses.SourceApproval',
  [EnrollmentSource.SelfEnroll]: 'courses.SourceSelfEnroll',
  [EnrollmentSource.LearningPath]: 'paths.SourceLearningPath',
};

/** "2026-09-14T10:00" for <input type="datetime-local"> from a UTC ISO string (shown in the viewer's local time). */
export function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** UTC ISO instant from a datetime-local value (interpreted in the viewer's time zone); null when blank. */
export function fromLocalInput(value?: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function hasUnlockRules(item: { releaseAt?: string | null; releaseAfterDays?: number | null; requiresPrevious?: boolean }): boolean {
  return !!item.releaseAt || !!item.releaseAfterDays || !!item.requiresPrevious;
}

/** Saves a blob through a temporary object URL. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ── L5: catalog ───────────────────────────────────────────────────────────
export const CATALOG_SORT_KEYS: Record<CatalogSort, string> = {
  [CatalogSort.Newest]: 'catalog.SortNewest',
  [CatalogSort.Rating]: 'catalog.SortRating',
  [CatalogSort.Popular]: 'catalog.SortPopular',
  [CatalogSort.Title]: 'catalog.SortTitle',
};

/** Label + icon for the catalog call to action. */
export const CATALOG_ACTION_META: Record<CatalogAction, { key: string; icon: string }> = {
  [CatalogAction.Preview]: { key: 'catalog.ActionPreview', icon: 'bi-eye' },
  [CatalogAction.Enroll]: { key: 'catalog.ActionEnroll', icon: 'bi-plus-circle' },
  [CatalogAction.RequestToJoin]: { key: 'catalog.ActionRequest', icon: 'bi-send' },
  [CatalogAction.Pending]: { key: 'catalog.ActionPending', icon: 'bi-hourglass-split' },
  [CatalogAction.Continue]: { key: 'catalog.ActionContinue', icon: 'bi-play-fill' },
  [CatalogAction.Review]: { key: 'catalog.ActionReview', icon: 'bi-arrow-repeat' },
  [CatalogAction.Closed]: { key: 'catalog.ActionClosed', icon: 'bi-lock' },
  [CatalogAction.Removed]: { key: 'catalog.ActionRemoved', icon: 'bi-slash-circle' },
  [CatalogAction.Unavailable]: { key: 'catalog.ActionUnavailable', icon: 'bi-slash-circle' },
};

// ── L6: content library ───────────────────────────────────────────────────
export const CONTENT_SCOPE_KEYS: Record<number, string> = {
  1: 'library.ScopePrivate',
  2: 'library.ScopeShared',
};

export const CONTENT_KIND_KEYS: Record<number, string> = {
  1: 'library.KindFile',
  2: 'library.KindVideoLink',
  3: 'library.KindEmbedLink',
};

/** Bootstrap icon for a library item (by kind, then file content type). */
export function libraryItemIcon(kind: number, contentType?: string | null): string {
  if (kind === 2) return 'bi-youtube';
  if (kind === 3) return 'bi-window';
  const ct = (contentType ?? '').toLowerCase();
  if (ct.startsWith('video/')) return 'bi-film';
  if (ct.startsWith('image/')) return 'bi-image';
  if (ct.startsWith('audio/')) return 'bi-music-note-beamed';
  if (ct === 'application/pdf') return 'bi-file-earmark-pdf';
  if (ct.includes('presentation') || ct.includes('powerpoint')) return 'bi-file-earmark-slides';
  if (ct.includes('spreadsheet') || ct.includes('excel') || ct === 'text/csv') return 'bi-file-earmark-spreadsheet';
  if (ct.includes('word')) return 'bi-file-earmark-word';
  if (ct === 'application/zip') return 'bi-file-earmark-zip';
  return 'bi-file-earmark';
}

/** Comma-separated tag input -> trimmed, distinct tags. */
export function parseTags(value?: string | null): string[] {
  return Array.from(new Set((value ?? '').split(',').map((t) => t.trim()).filter((t) => t.length > 0)));
}

export const LIBRARY_ACCEPT =
  '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.zip,.jpg,.jpeg,.png,.webp,.gif,.mp3,.m4a,.wav,.mp4,.m4v,.webm,.ogv,.mov';
